import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";

/* Tracks whether we're on a mobile-width viewport, live-updating on resize. */
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

/* ─────────────────────────────────────────────────────────────
   CityPicker — location search backed by OpenStreetMap Nominatim.

   Why this replaces the old fixed-list dropdown:
   The previous version only offered ~32 hard-coded Tamil Nadu
   cities. Typing anything else (a small town, a different state,
   a locality) had no match. This version searches Nominatim live
   as the user types, covering every city/town/village/suburb
   OpenStreetMap knows about — not just a curated list.

   Props (unchanged from the old CityPicker so Header.js/Home.js
   don't need prop-shape changes):
     - cities:      optional string[] of quick-suggestion chips
                     shown before the user types anything (e.g. a
                     "recent/popular" list). Purely cosmetic now —
                     no longer used to validate/restrict search.
     - value:        currently selected place name (string)
     - onChange:     (placeName: string) => void
     - onDetectGPS:  () => void  — parent still owns geolocation +
                     reverse-geocoding; this component just shows
                     a "use my location" button that calls it.
     - detecting:    bool, shows a spinner state while GPS runs
────────────────────────────────────────────────────────────── */

const NOMINATIM_SEARCH_URL = "https://nominatim.openstreetmap.org/search";

/* Debounce helper */
function useDebouncedValue(value, delayMs) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

/* Pull the most sensible short label out of a Nominatim result
   (prefer town/village/suburb name over the full display_name) */
const labelFor = (result) => {
  const a = result.address || {};
  const primary =
    a.city || a.town || a.village || a.suburb || a.neighbourhood ||
    a.municipality || a.county || result.name;
  const state = a.state;
  if (primary && state && primary !== state) return `${primary}, ${state}`;
  return primary || result.display_name;
};

const CityPicker = ({ cities = [], value, onChange, onDetectGPS, detecting = false }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");
  const wrapRef = useRef(null);
  const debouncedQuery = useDebouncedValue(query, 350);
  const abortRef = useRef(null);
  const isMobile = useIsMobile();
  // Controls the slide-in/out animation of the mobile bottom sheet,
  // separate from `open` so the close transition can finish before unmount.
  const [sheetVisible, setSheetVisible] = useState(false);

  /* Animate the mobile sheet in right after it mounts. */
  useEffect(() => {
    if (open && isMobile) {
      const id = requestAnimationFrame(() => setSheetVisible(true));
      return () => cancelAnimationFrame(id);
    }
    setSheetVisible(false);
  }, [open, isMobile]);

  /* Closes the picker — animates the sheet down first on mobile so it
     doesn't just vanish. */
  const requestClose = useCallback(() => {
    if (isMobile) {
      setSheetVisible(false);
      setTimeout(() => setOpen(false), 220);
    } else {
      setOpen(false);
    }
  }, [isMobile]);

  /* Close dropdown on outside click.
     On mobile the sheet is rendered via a portal straight into
     document.body, so its DOM nodes are NOT inside wrapRef anymore —
     wrapRef.contains() would wrongly say "outside" for every click made
     inside the sheet itself (including the input), closing it instantly.
     So on mobile we skip this listener entirely and rely only on the
     explicit close triggers (backdrop tap, drag handle, selecting a
     result) instead. */
  useEffect(() => {
    if (isMobile) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) requestClose();
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [requestClose, isMobile]);

  /* Instant local fallback — filters the built-in city list as the user
     types, with zero network dependency. Always available immediately,
     so search still works even if the live API below is slow, blocked,
     or rate-limited. */
  const localMatches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return cities.filter((c) => c.toLowerCase().includes(q)).slice(0, 8);
  }, [query, cities]);

  /* Live search against Nominatim, scoped to India, as the user types.
     No fixed list — any place OSM has (city, town, village, suburb...)
     can match, so small towns work exactly like major cities. This is
     additive on top of localMatches above, and failing silently here
     (e.g. the API being rate-limited/blocked) no longer breaks search —
     the local list still works. */
  useEffect(() => {
    const q = debouncedQuery.trim();
    if (q.length < 2) {
      setResults([]);
      setSearching(false);
      setError("");
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setSearching(true);
    setError("");

    const params = new URLSearchParams({
      q,
      format: "jsonv2",
      addressdetails: "1",
      countrycodes: "in",   // scope to India — drop this line for global search
      limit: "8",
    });

    fetch(`${NOMINATIM_SEARCH_URL}?${params.toString()}`, {
      signal: controller.signal,
      headers: { Accept: "application/json" },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        // Keep only place-like results (skip roads, shops, etc.)
        const placeTypes = new Set([
          "city", "town", "village", "suburb", "neighbourhood",
          "hamlet", "municipality", "county", "administrative",
        ]);
        const filtered = Array.isArray(data)
          ? data.filter((r) => placeTypes.has(r.type) || placeTypes.has(r.addresstype))
          : [];
        setResults((filtered.length ? filtered : data || []).slice(0, 8));
      })
      .catch((err) => {
        if (err.name !== "AbortError") {
          // Live search failed (blocked/rate-limited/offline) — stay quiet
          // here since localMatches above still gives usable results; only
          // surface an error if there's nothing local to fall back on.
          setResults([]);
          setError(localMatches.length ? "" : "Couldn't reach the location search — showing nearby matches only.");
        }
      })
      .finally(() => setSearching(false));

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  const handleSelect = (label) => {
    onChange?.(label);
    setQuery("");
    setResults([]);
    requestClose();
  };

  const handleDetect = () => {
    requestClose();
    onDetectGPS?.();
  };

  /* Shared body — identical content, whether shown in the desktop
     dropdown card or the mobile bottom sheet. */
  const pickerBody = (
    <>
      <div style={{ position: "relative", marginBottom: 8 }}>
        <i className="fa-solid fa-magnifying-glass" style={{
          position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
          fontSize: 12, color: "#999",
        }} />
        <input
          type="text"
          autoFocus={!isMobile}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search any city, town, or village…"
          style={{
            width: "100%", padding: "8px 10px 8px 30px", borderRadius: 8,
            border: "1px solid #ddd", fontSize: 13, outline: "none",
            boxSizing: "border-box",
          }}
        />
      </div>

      <button
        type="button"
        onClick={handleDetect}
        disabled={detecting}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: 8,
          padding: "8px 10px", borderRadius: 8, border: "1px solid #e0f2f1",
          background: "#f0faf9", color: "#00796b", fontSize: 12.5, fontWeight: 600,
          cursor: detecting ? "wait" : "pointer", marginBottom: 8,
        }}
      >
        <i className={`fa-solid ${detecting ? "fa-spinner fa-spin" : "fa-location-crosshairs"}`} />
        {detecting ? "Detecting your location…" : "Use my current location"}
      </button>

      {/* Search state */}
      {searching && (
        <div style={{ padding: "6px 4px", fontSize: 12, color: "#999" }}>Searching…</div>
      )}
      {error && (
        <div style={{ padding: "6px 4px", fontSize: 12, color: "#c62828" }}>{error}</div>
      )}

      {/* Instant local matches — filtered from the built-in city list,
          shown immediately with no network round-trip. */}
      {query.trim() && localMatches.length > 0 && (
        <div style={{ maxHeight: isMobile ? "none" : 220, overflowY: isMobile ? "visible" : "auto" }}>
          {localMatches.map((c) => (
            <button
              type="button"
              key={`local-${c}`}
              onClick={() => handleSelect(c)}
              style={{
                display: "block", width: "100%", textAlign: "left",
                padding: "8px 10px", borderRadius: 8, border: "none",
                background: "transparent", cursor: "pointer", fontSize: 13, fontWeight: 500,
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#f7faf8")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      {/* Live results — additional matches from the wider live search,
          skipping anything already shown above from the local list. */}
      {!searching && results.length > 0 && (
        <div style={{ maxHeight: isMobile ? "none" : 220, overflowY: isMobile ? "visible" : "auto" }}>
          {results
            .filter((r) => !localMatches.some((c) => c.toLowerCase() === labelFor(r).toLowerCase()))
            .map((r) => {
            const label = labelFor(r);
            return (
              <button
                type="button"
                key={r.place_id}
                onClick={() => handleSelect(label)}
                style={{
                  display: "block", width: "100%", textAlign: "left",
                  padding: "8px 10px", borderRadius: 8, border: "none",
                  background: "transparent", cursor: "pointer", fontSize: 13,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "#f7faf8")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                <div style={{ fontWeight: 500 }}>{label}</div>
                <div style={{ fontSize: 11, color: "#999", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.display_name}
                </div>
              </button>
            );
          })}
        </div>
      )}


      {/* Quick suggestions — shown only before typing, purely cosmetic */}
      {!query && cities.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#aaa", textTransform: "uppercase", padding: "6px 4px 4px" }}>
            Popular
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "0 4px" }}>
            {cities.slice(0, 12).map((c) => (
              <button
                type="button"
                key={c}
                onClick={() => handleSelect(c)}
                style={{
                  padding: "5px 10px", borderRadius: 20, border: "1px solid #eee",
                  background: value === c ? "#e8f5e9" : "#fafafa",
                  color: value === c ? "#2e7d32" : "#444",
                  fontSize: 12, cursor: "pointer",
                }}
              >
                {c}
              </button>
            ))}
          </div>
        </>
      )}
    </>
  );

  return (
    <div className="city-picker-wrap" ref={wrapRef} style={{ position: "relative" }}>
      <button
        type="button"
        className="city-picker-trigger"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "flex", alignItems: "center", gap: 6,
          background: "none", border: "none", cursor: "pointer",
          fontSize: 13.5, fontWeight: 600, color: "inherit", padding: 0,
        }}
      >
        <i className="fa-solid fa-location-dot" style={{ color: "#c2185b" }} />
        <span style={{ maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {detecting ? "Detecting…" : (value || "Select location")}
        </span>
        <i className={`fa-solid fa-chevron-${open ? "up" : "down"}`} style={{ fontSize: 10, opacity: 0.6 }} />
      </button>

      {/* Desktop: small anchored dropdown card, unchanged from before */}
      {open && !isMobile && (
        <div
          className="city-picker-dropdown"
          style={{
            position: "absolute", top: "calc(100% + 8px)", left: 0,
            width: 300, background: "#fff", borderRadius: 12,
            boxShadow: "0 12px 32px rgba(0,0,0,0.14)", border: "1px solid #eee",
            zIndex: 9999, padding: 10,
          }}
        >
          {pickerBody}
        </div>
      )}

      {/* Mobile: full-width bottom sheet that slides up from the bottom,
          rendered via a portal so it isn't clipped by any ancestor's
          overflow/transform and always covers the full viewport. */}
      {open && isMobile && createPortal(
        <>
          <div
            className="city-picker-backdrop"
            onClick={requestClose}
            style={{
              position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
              zIndex: 10000, opacity: sheetVisible ? 1 : 0,
              transition: "opacity 220ms ease",
            }}
          />
          <div
            className="city-picker-sheet"
            role="dialog"
            aria-modal="true"
            style={{
              position: "fixed", left: 0, right: 0, bottom: 0,
              maxHeight: "85vh", background: "#fff",
              borderRadius: "18px 18px 0 0",
              boxShadow: "0 -8px 32px rgba(0,0,0,0.2)",
              zIndex: 10001, padding: "10px 16px calc(16px + env(safe-area-inset-bottom))",
              transform: sheetVisible ? "translateY(0)" : "translateY(100%)",
              transition: "transform 260ms cubic-bezier(.32,.72,0,1)",
              overflowY: "auto",
              boxSizing: "border-box",
            }}
          >
            <div
              onClick={requestClose}
              style={{ width: 40, height: 4, borderRadius: 2, background: "#ddd", margin: "0 auto 14px", cursor: "pointer" }}
            />
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 10, color: "#222" }}>
              Select location
            </div>
            {pickerBody}
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default CityPicker;